import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { FilterState } from './types';
import type { FilterOptions } from './deriveFilterOptions';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;

interface Props {
  value: FilterState;
  options: FilterOptions;
  initial: FilterState;
  onChange: (next: FilterState) => void;
}

const toDateInput = (iso: string) => iso.slice(0, 10);

const shiftFrom = (to: string, windowMs: number): string =>
  new Date(new Date(`${to}T00:00:00Z`).getTime() - windowMs).toISOString().slice(0, 10);

export const AnalyzePrintingsFilters = ({ value, options, initial, onChange }: Props) => {
  const systemSelected = value.systemId !== null;
  const activeWindow = (windowMs: number) => value.from === shiftFrom(value.to, windowMs);

  // Excluded systems disappear from the result rows by design, so they would
  // also disappear from the options derived from those rows — leaving the
  // user no way to un-exclude them. Merge the currently-selected exclusions
  // back into the menu items so they can always be toggled off.
  const excludedSystemMenuOptions = Array.from(
    new Set([...options.excludedSystemNames, ...value.excludedSystemNames])
  ).sort((a, b) => a.localeCompare(b));

  // Resolve the current ID-based filter values back to their corresponding
  // option object for the Autocomplete `value` prop. The selected option is
  // always present in the fetched rows (we filter BY it), so the lookup
  // returns the correct object.
  const selectedMachine = options.machines.find((m) => m.id === value.machineId) ?? null;
  const selectedSystem = options.systems.find((s) => s.id === value.systemId) ?? null;
  const selectedSystemUser = options.systemUsers.find((u) => u.id === value.systemUserId) ?? null;

  return (
    <Box mb={2}>
      <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={1}>
        <Tooltip title="Reset filters">
          <IconButton onClick={() => onChange(initial)} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
        <TextField
          size="small"
          type="date"
          label="From *"
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160, flexShrink: 0 }}
          value={toDateInput(value.from)}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
        />
        <TextField
          size="small"
          type="date"
          label="To *"
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160, flexShrink: 0 }}
          value={toDateInput(value.to)}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
        />
        <Autocomplete
          size="small"
          sx={{ width: 200, flexShrink: 0 }}
          options={options.machines}
          value={selectedMachine}
          getOptionLabel={(opt) => opt?.serial ?? ''}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          onChange={(_e, next) => onChange({ ...value, machineId: next?.id ?? null })}
          renderInput={(params) => <TextField {...params} label="Machine" />}
        />
        <Autocomplete
          size="small"
          sx={{ width: 200, flexShrink: 0 }}
          options={options.systems}
          value={selectedSystem}
          getOptionLabel={(opt) => opt?.releaseName ?? ''}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          onChange={(_e, next) =>
            onChange({
              ...value,
              systemId: next?.id ?? null,
              // Selecting a single system clears any active exclusions
              // (they're mutually exclusive in the legacy implementation).
              excludedSystemNames: next ? [] : value.excludedSystemNames,
            })
          }
          renderInput={(params) => <TextField {...params} label="System" />}
        />
        <FormControl size="small" sx={{ width: 220, flexShrink: 0 }} disabled={systemSelected}>
          <InputLabel>Excluded systems</InputLabel>
          <Select
            multiple
            label="Excluded systems"
            value={value.excludedSystemNames}
            renderValue={(selected) => (selected as string[]).join(', ')}
            onChange={(e) => {
              const v = e.target.value as string[] | string;
              onChange({
                ...value,
                excludedSystemNames: Array.isArray(v) ? v : v.split(','),
              });
            }}
          >
            {excludedSystemMenuOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          size="small"
          sx={{ width: 200, flexShrink: 0 }}
          options={options.systemUsers}
          value={selectedSystemUser}
          getOptionLabel={(opt) => opt?.username ?? ''}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          onChange={(_e, next) => onChange({ ...value, systemUserId: next?.id ?? null })}
          renderInput={(params) => <TextField {...params} label="System User" />}
        />
      </Box>
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant={activeWindow(DAY_MS) ? 'contained' : 'outlined'}
          onClick={() => onChange({ ...value, from: shiftFrom(value.to, DAY_MS) })}
        >
          Day
        </Button>
        <Button
          size="small"
          variant={activeWindow(WEEK_MS) ? 'contained' : 'outlined'}
          onClick={() => onChange({ ...value, from: shiftFrom(value.to, WEEK_MS) })}
        >
          Week
        </Button>
        <Button
          size="small"
          variant={activeWindow(MONTH_MS) ? 'contained' : 'outlined'}
          onClick={() => onChange({ ...value, from: shiftFrom(value.to, MONTH_MS) })}
        >
          Month
        </Button>
      </Box>
    </Box>
  );
};
